<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { toDatesArray } from "@/composables/useTrekPackageSummary";
import { useTrekPackagesApi } from "@/composables/useTrekPackagesApi";
import { formatTrekDetails } from "@/utils/formatTrekDetails";

/**
 * Supabase / enquiries-API backed Trek Packages UI.
 * Keep AppTrekPackages.vue (Kirby) untouched for easy revert — swap mounts only.
 * Images still come from Kirby page state until they are migrated to Supabase.
 */
const props = defineProps<{ trekTitle: string }>();
const page = usePage();

const { data: apiPayload, pending: apiPending, error: apiError } =
  useTrekPackagesApi(() => props.trekTitle);

function normalizeString(str: string | null | undefined) {
  return String(str ?? "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "");
}

/** Prefer Kirby CMS trek_image (still the source of truth for package photos). */
function kirbyImageForPackage(trek: {
  trek_title?: string | null;
  trek_route?: string | null;
  trek_code?: string | null;
}) {
  const list = page.value?.trekPackagesList ?? [];
  if (!list.length) return null;

  const wantedTitle = normalizeString(trek.trek_title);
  const wantedRoute = normalizeString(trek.trek_route);

  const match =
    list.find((kirby) => {
      const title = normalizeString(kirby?.trek_title);
      const route = normalizeString(kirby?.trek_route);
      if (!wantedTitle || title !== wantedTitle) return false;
      // Unlabelled Kirby rows are TMB defaults
      if (!route || !wantedRoute) return true;
      return (
        route === wantedRoute ||
        wantedRoute.includes(route) ||
        route.includes(wantedRoute)
      );
    }) ||
    list.find(
      (kirby) => normalizeString(kirby?.trek_title) === wantedTitle,
    );

  return match?.trek_image?.url ? match.trek_image : null;
}

const trekPackages = computed(() => {
  const normalizedTrekTitle = normalizeString(props.trekTitle);
  if (!normalizedTrekTitle) return [];
  const list = apiPayload.value?.trekPackagesList ?? [];
  return list
    .filter((trek) => {
      const normalizedRoute = normalizeString(trek.trek_route);
      if (!normalizedRoute) {
        return (
          normalizedTrekTitle.includes("tour du mont blanc") ||
          normalizedTrekTitle === "tmb"
        );
      }
      return (
        normalizedRoute === normalizedTrekTitle ||
        normalizedTrekTitle.includes(normalizedRoute) ||
        normalizedRoute.includes(normalizedTrekTitle)
      );
    })
    .map((trek) => {
      const kirbyImage = kirbyImageForPackage(trek);
      if (!kirbyImage) return trek;
      return { ...trek, trek_image: kirbyImage };
    });
});

/** Match price rows by trek_code (stable), then title, then index. */
function priceForPackage(
  prices: any[] | undefined,
  trek: any,
  index: number,
) {
  const list = prices ?? [];
  if (trek?.trek_code) {
    const byCode = list.find(
      (p) => String(p.trek_code || "") === String(trek.trek_code),
    );
    if (byCode) return byCode;
  }
  if (trek?.trek_title) {
    const wanted = normalizeString(trek.trek_title);
    const byTitle = list.find(
      (p) => normalizeString(p.trek_title) === wanted,
    );
    if (byTitle) return byTitle;
  }
  return list[index] ?? null;
}

const apiYears = computed(() => apiPayload.value?.years ?? []);

const trekPackagePrices1 = computed(() => {
  const year = apiYears.value[0];
  return year?.prices ?? apiPayload.value?.trekprices1 ?? [];
});
const trekPackagePrices2 = computed(() => {
  const year = apiYears.value[1];
  return year?.prices ?? apiPayload.value?.trekprices2 ?? [];
});

/** One price cell per package card — aligned by trek_code / title. */
const priceRowsYear1 = computed(() =>
  trekPackages.value.map((trek, index) =>
    priceForPackage(trekPackagePrices1.value, trek, index),
  ),
);
const priceRowsYear2 = computed(() =>
  trekPackages.value.map((trek, index) =>
    priceForPackage(trekPackagePrices2.value, trek, index),
  ),
);

/** CMS keeps a price row per package for both years; treat blank rows as no package for that year. */
function hasPriceData(trek: {
  trek_price?: string | null;
  availability?: string | null;
  datesAvailability?: unknown[] | null;
}): boolean {
  const price = String(trek?.trek_price ?? "").trim();
  const availability = String(trek?.availability ?? "").trim();
  const dates = trek?.datesAvailability;
  return !!(
    price ||
    availability ||
    (Array.isArray(dates) && dates.length > 0)
  );
}

const hasYear1Data = computed(() =>
  priceRowsYear1.value.some((trek) => trek && hasPriceData(trek)),
);
const hasYear2Data = computed(() =>
  priceRowsYear2.value.some((trek) => trek && hasPriceData(trek)),
);
const hasMultipleYears = computed(
  () => hasYear1Data.value && hasYear2Data.value,
);

const year1Label = computed(
  () => apiYears.value[0]?.label || apiPayload.value?.setYear1 || "",
);
const year2Label = computed(
  () => apiYears.value[1]?.label || apiPayload.value?.setYear2 || "",
);

// Prefer year 1 when it has data; apply once after API payload arrives.
const activeYear = ref<1 | 2>(1);
const yearDefaultApplied = ref(false);

watch(
  () => apiPayload.value,
  (payload) => {
    if (!payload || yearDefaultApplied.value) return;
    // Wait a tick so hasYear* computeds see the new payload.
    queueMicrotask(() => {
      if (yearDefaultApplied.value) return;
      if (!hasYear1Data.value && !hasYear2Data.value) return;
      yearDefaultApplied.value = true;
      activeYear.value = hasYear1Data.value ? 1 : 2;
    });
  },
  { immediate: true },
);

const setYear1 = computed(() => activeYear.value === 1);
const setYear2 = computed(() => activeYear.value === 2);

const showYear1 = () => {
  if (!hasYear1Data.value) return;
  activeYear.value = 1;
};

const showYear2 = () => {
  if (!hasYear2Data.value) return;
  activeYear.value = 2;
};

const trekPackagesLength = computed(() => {
  return trekPackages.value.length;
});

const currentTrek = ref<(typeof trekPackages.value)[0] | null>(null);
const currentTrekNum = ref(0);
const showPackageModal = ref(false);
const showForm = ref(false);
const showEnquiryModal = ref(false);

/** CMS sometimes stores trek_link with trailing newlines → /path%0A 404s. */
const currentTrekItineraryPath = computed(() => {
  const link = currentTrek.value?.trek_link;
  if (typeof link !== "string") return "";
  return link.trim();
});

const togglePackageModal = (
  trek: (typeof trekPackages.value)[0],
  index: number,
) => {
  showPackageModal.value = !showPackageModal.value;
  currentTrek.value = trek;
  currentTrekNum.value = index;
};

function openPackageModal(
  trek: (typeof trekPackages.value)[0],
  index: number,
  options?: { preferYear2?: boolean },
) {
  currentTrek.value = trek;
  currentTrekNum.value = index;
  showPackageModal.value = true;
  showForm.value = false;
  showEnquiryModal.value = false;

  const price2 = priceForPackage(trekPackagePrices2.value, trek, index);
  const price1 = priceForPackage(trekPackagePrices1.value, trek, index);
  const dates2 = toDatesArray(price2?.datesAvailability);
  const dates1 = toDatesArray(price1?.datesAvailability);

  if (options?.preferYear2 && dates2.length && hasYear2Data.value) {
    showYear2();
  } else if (dates2.length && hasYear2Data.value && !dates1.length) {
    showYear2();
  } else if (dates1.length && hasYear1Data.value) {
    showYear1();
  } else if (options?.preferYear2 && hasYear2Data.value) {
    showYear2();
  }
}

const { request, clearOpenPackageModalRequest } = useOpenTrekPackageModal();

watch(
  request,
  (req) => {
    if (!req) return;
    const packages = trekPackages.value;
    if (!packages.length) return;

    let index = -1;
    if (req.packageId != null && req.packageId !== "") {
      index = packages.findIndex(
        (trek) =>
          String(trek.id) === String(req.packageId) ||
          String(trek.trek_code) === String(req.packageId),
      );
    }
    if (index < 0 && req.packageTitle) {
      const wanted = normalizeString(req.packageTitle);
      index = packages.findIndex(
        (trek) => normalizeString(trek.trek_title) === wanted,
      );
      if (index < 0) {
        index = packages.findIndex((trek) =>
          normalizeString(trek.trek_title).includes(wanted),
        );
      }
    }

    if (index >= 0 && packages[index]) {
      openPackageModal(packages[index], index, {
        preferYear2: !!req.preferYear2,
      });
    }
    clearOpenPackageModalRequest();
  },
  { flush: "post" },
);

const closePackageModal = () => {
  showPackageModal.value = false;
  showForm.value = false;
};

const enquiryTrekRoute = computed(
  () => currentTrek.value?.trek_route?.trim() || props.trekTitle || "",
);
const enquiryTrekTitle = computed(
  () => currentTrek.value?.trek_title?.trim() || "",
);
const enquiryPriceRow = computed(() => {
  if (!currentTrek.value) return null;
  if (shouldShowDatesForSelfGuided.value) {
    return setYear1.value
      ? selfGuidedTrekPrice1.value
      : selfGuidedTrekPrice2.value;
  }
  const list = setYear1.value
    ? trekPackagePrices1.value
    : trekPackagePrices2.value;
  return (
    priceForPackage(list, currentTrek.value, currentTrekNum.value) ?? null
  );
});
const enquiryPrice = computed(
  () => String(enquiryPriceRow.value?.trek_price ?? "").trim(),
);
const enquiryPersonDay = computed(
  () => String(enquiryPriceRow.value?.trek_person_day ?? "").trim(),
);
const enquiryAvailability = computed(
  () => String(enquiryPriceRow.value?.availability ?? "").trim(),
);
const enquiryNextDate = computed(() => {
  const dates = enquiryPriceRow.value?.datesAvailability;
  if (Array.isArray(dates)) {
    const next = dates.find((item) => {
      const availability = String(item?.availability ?? "")
        .trim()
        .toLowerCase();
      return (
        item?.dates &&
        availability !== "fully booked" &&
        availability !== "no availability"
      );
    });
    const fromList = String(next?.dates ?? "").trim();
    if (fromList) return fromList;
  }
  // Self-guided: CMS stores flexible copy in HTML `dates`, not datesAvailability.
  return String(enquiryPriceRow.value?.dates ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
});
const enquirySpaces = computed(() => {
  const dates = enquiryPriceRow.value?.datesAvailability;
  if (!Array.isArray(dates)) return "";
  const next = dates.find((item) => {
    const availability = String(item?.availability ?? "")
      .trim()
      .toLowerCase();
    return (
      item?.dates &&
      availability !== "fully booked" &&
      availability !== "no availability"
    );
  });
  if (next?.spacesAvailable) {
    return `${next.spacesAvailable} space${next.spacesAvailable === "1" ? "" : "s"} available`;
  }
  if (next?.limitedAvailability) {
    return `${next.limitedAvailability} space${next.limitedAvailability === "1" ? "" : "s"} available`;
  }
  return "";
});

function openEnquiryModal() {
  showEnquiryModal.value = true;
}
function closeEnquiryModal() {
  showEnquiryModal.value = false;
}
function onEnquirySuccess() {
  closeEnquiryModal();
  closePackageModal();
}

// Check if this is the specific self-guided trek that should show dates/availability
const shouldShowDatesForSelfGuided = computed(() => {
  if (!currentTrek.value) return false;
  const title = currentTrek.value.trek_title?.toLowerCase() || "";
  const route = currentTrek.value.trek_route?.toLowerCase() || "";

  // Check if route is "Tour du Mont Blanc" and title is "Full Self-Guided Trek"
  return (
    route.includes("tour du mont blanc") &&
    title.includes("full self-guided trek")
  );
});

// Find the trek in prices array for self-guided treks
const selfGuidedTrekPrice1 = computed(() => {
  if (!shouldShowDatesForSelfGuided.value || !currentTrek.value) return null;
  return priceForPackage(
    trekPackagePrices1.value,
    currentTrek.value,
    currentTrekNum.value,
  );
});

const selfGuidedTrekPrice2 = computed(() => {
  if (!shouldShowDatesForSelfGuided.value || !currentTrek.value) return null;
  return priceForPackage(
    trekPackagePrices2.value,
    currentTrek.value,
    currentTrekNum.value,
  );
});

/** Price row for the open package (trek_code first, then array index). */
const currentPriceYear1 = computed(() => {
  if (!currentTrek.value) return null;
  if (shouldShowDatesForSelfGuided.value) return selfGuidedTrekPrice1.value;
  return priceForPackage(
    trekPackagePrices1.value,
    currentTrek.value,
    currentTrekNum.value,
  );
});

const currentPriceYear2 = computed(() => {
  if (!currentTrek.value) return null;
  if (shouldShowDatesForSelfGuided.value) return selfGuidedTrekPrice2.value;
  return priceForPackage(
    trekPackagePrices2.value,
    currentTrek.value,
    currentTrekNum.value,
  );
});

const currentDatesYear1 = computed(() =>
  toDatesArray(currentPriceYear1.value?.datesAvailability),
);

const currentDatesYear2 = computed(() =>
  toDatesArray(currentPriceYear2.value?.datesAvailability),
);

/** True when this package title is any self-guided trek. */
const currentIsSelfGuidedTitle = computed(() =>
  (currentTrek.value?.trek_title?.toLowerCase() || "").includes("self-guided"),
);

/**
 * Fixed departure table — hide for non-flagship self-guided (those show an image).
 * Full Self-Guided shows the table when CMS has datesAvailability rows.
 */
const showDatesTableYear1 = computed(() => {
  if (!setYear1.value || !currentDatesYear1.value.length) return false;
  if (currentIsSelfGuidedTitle.value) return shouldShowDatesForSelfGuided.value;
  return true;
});

const showDatesTableYear2 = computed(() => {
  if (!setYear2.value || !currentDatesYear2.value.length) return false;
  if (currentIsSelfGuidedTitle.value) return shouldShowDatesForSelfGuided.value;
  return true;
});

/**
 * Self-guided (and any package without date rows) stores flexible copy in `dates`
 * HTML plus a top-level availability status — show that instead of an empty block.
 */
function hasFlexibleAvailability(
  price: {
    availability?: string | null;
    dates?: string | null;
  } | null,
): boolean {
  if (!price) return false;
  return !!(
    String(price.availability ?? "").trim() || String(price.dates ?? "").trim()
  );
}

const showFlexibleAvailabilityYear1 = computed(() => {
  if (!setYear1.value || currentDatesYear1.value.length) return false;
  if (currentIsSelfGuidedTitle.value && !shouldShowDatesForSelfGuided.value) {
    return false;
  }
  return hasFlexibleAvailability(currentPriceYear1.value);
});

const showFlexibleAvailabilityYear2 = computed(() => {
  if (!setYear2.value || currentDatesYear2.value.length) return false;
  if (currentIsSelfGuidedTitle.value && !shouldShowDatesForSelfGuided.value) {
    return false;
  }
  return hasFlexibleAvailability(currentPriceYear2.value);
});

// Group dates by month for self-guided treks
const getGroupedDates = (datesAvailability) => {
  const arr = toDatesArray(datesAvailability);
  if (!arr.length) return [];

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const grouped = [];
  let currentMonth = null;

  arr.forEach((item) => {
    // Extract month from date string (e.g., "10 - 22 Aug" or "10 Aug - 22 Aug")
    const dateStr = item.dates || "";
    const monthMatch = dateStr.match(
      /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\b/i,
    );

    if (monthMatch) {
      const monthAbbr = monthMatch[0].substring(0, 3).toLowerCase();
      const monthIndex = [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "aug",
        "sep",
        "oct",
        "nov",
        "dec",
      ].indexOf(monthAbbr);

      if (monthIndex !== -1 && currentMonth !== monthIndex) {
        currentMonth = monthIndex;
        grouped.push({
          type: "month-header",
          month: monthNames[monthIndex],
          monthIndex,
        });
      }
    }

    grouped.push({
      type: "date-item",
      ...item,
    });
  });

  return grouped;
};

const selfGuidedDatesYear1 = computed(() => {
  if (!shouldShowDatesForSelfGuided.value || !selfGuidedTrekPrice1.value)
    return [];
  return getGroupedDates(selfGuidedTrekPrice1.value.datesAvailability);
});

const selfGuidedDatesYear2 = computed(() => {
  if (!shouldShowDatesForSelfGuided.value || !selfGuidedTrekPrice2.value)
    return [];
  return getGroupedDates(selfGuidedTrekPrice2.value.datesAvailability);
});

/** Flagship TMB full self-guided — match by code, then package id/title. */
const isSelfGuidedTrek = (
  trekOrId:
    | { id?: string | number | null; trek_code?: string | null; trek_title?: string | null; trek_route?: string | null }
    | string
    | number
    | null
    | undefined,
) => {
  if (trekOrId == null || trekOrId === "") return false;
  const pkg =
    typeof trekOrId === "object"
      ? trekOrId
      : trekPackages.value.find(
          (t) =>
            (t.id != null && String(t.id) === String(trekOrId)) ||
            (t.trek_code != null && String(t.trek_code) === String(trekOrId)),
        );
  if (!pkg) return false;
  const route = pkg.trek_route?.toLowerCase() || "";
  const title = pkg.trek_title?.toLowerCase() || "";
  return (
    route.includes("tour du mont blanc") &&
    title.includes("full self-guided trek")
  );
};

</script>

<template>
  <Teleport to="body">
    <Transition name="pop" appear>
      <div v-if="showPackageModal && !showEnquiryModal" v-cloak class="modal trek-package-info">
        <button class="close-modal" @click="closePackageModal"></button>
        <AppSVGBrandGraphic />
        <div v-if="currentTrek && !showForm" class="trek-details-wrapper">
          <div class="wrapper">
            <div class="trek-details">
              <div class="trek-image">
                <NuxtPicture
                  v-if="currentTrek?.trek_image"
                  loading="lazy"
                  fit="inside"
                  :src="currentTrek.trek_image.url"
                  :width="700"
                  :height="400"
                  :alt="currentTrek.trek_image.alt"
                  sizes="sm:100vw md:50vw lg:600px"
                />
                <header>
                  <h4>{{ currentTrek?.trek_route }}</h4>
                  <h3>{{ currentTrek?.trek_title }}</h3>
                </header>
              </div>
              <div>
                <div v-html="formatTrekDetails(currentTrek?.trek_details)"></div>
              </div>
              <div class="trek-price">
                <section class="trek-price">
                  <template v-if="setYear1 && currentPriceYear1">
                    <div :key="currentPriceYear1?.id || 'y1'">
                      <p
                        v-if="shouldShowDatesForSelfGuided"
                        class="from-price"
                      >
                        From
                      </p>
                      <p class="price">
                        &pound;{{
                          String(currentPriceYear1?.trek_price ?? "").trim()
                        }}
                      </p>
                      <p class="per">{{ currentPriceYear1?.trek_person_day }}</p>
                    </div>
                  </template>
                  <template v-if="setYear2 && currentPriceYear2">
                    <div :key="currentPriceYear2?.id || 'y2'">
                      <p
                        v-if="shouldShowDatesForSelfGuided"
                        class="from-price"
                      >
                        From
                      </p>
                      <p class="price">
                        &pound;{{
                          String(currentPriceYear2?.trek_price ?? "").trim()
                        }}
                      </p>
                      <p class="per">{{ currentPriceYear2?.trek_person_day }}</p>
                    </div>
                  </template>
                </section>
              </div>
            </div>
            <footer class="display-mobile">
              <NuxtLink
                v-if="currentTrekItineraryPath"
                :to="currentTrekItineraryPath"
                class="btn"
                @click="closePackageModal"
              >
                View trek itinerary
              </NuxtLink>
              <NuxtLink
                to="/essential-info/booking-your-trek"
                class="ready-to-book"
                @click="closePackageModal"
              >
                Ready to book?
              </NuxtLink>
              <button
                type="button"
                class="btn enquire-btn"
                @click="openEnquiryModal"
              >
                Enquire now
              </button>
            </footer>
            <div class="trek-info-wrapper">
              <div class="packages-dates-availability">
                <template v-if="showDatesTableYear1">
                  <h4>Dates &amp; Availability for {{ year1Label }}</h4>

                  <template v-if="shouldShowDatesForSelfGuided">
                    <div
                      class="dates-table"
                      :class="{
                        'self-guided-two-column': shouldShowDatesForSelfGuided,
                      }"
                    >
                      <template
                        v-for="(item, index) in selfGuidedDatesYear1"
                        :key="index"
                      >
                        <div
                          v-if="item.type === 'month-header'"
                          class="month-header"
                        >
                          {{ item.month }}
                        </div>
                        <div
                          v-else-if="item.type === 'date-item'"
                          class="table-row"
                        >
                          <div class="dates">{{ item.dates }}</div>
                          <div
                            class="availability"
                            :class="
                              item?.availability
                                ? item.availability
                                    .toLowerCase()
                                    .replace(/\s+/g, '')
                                : ''
                            "
                          >
                            <span
                              v-if="
                                item.availability === 'Fully Booked' ||
                                item.availability === 'No Availability'
                              "
                            >
                              {{ item.availability }}
                            </span>
                            <span v-else-if="item.spacesAvailable">
                              {{ item.spacesAvailable }} space<span
                                v-if="item.spacesAvailable !== '1'"
                                >s</span
                              >
                              available</span
                            >
                            <span v-else-if="item.limitedAvailability">
                              {{ item.limitedAvailability }} space<span
                                v-if="item.limitedAvailability !== '1'"
                                >s</span
                              >
                              available</span
                            >
                            <span v-else> {{ item.availability }}</span>
                          </div>
                        </div>
                      </template>
                    </div>
                  </template>
                  <template v-else>
                    <div class="dates-table">
                      <div
                        v-for="(item, dateIndex) in currentDatesYear1"
                        :key="item.id ?? dateIndex"
                        class="table-row"
                      >
                        <div class="dates">{{ item.dates }}</div>
                        <div
                          class="availability"
                          :class="
                            item?.availability
                              ? item.availability
                                  .toLowerCase()
                                  .replace(/\s+/g, '')
                              : ''
                          "
                        >
                          <span
                            v-if="
                              item.availability === 'Fully Booked' ||
                              item.availability === 'No Availability'
                            "
                          >
                            {{ item.availability }}
                          </span>
                          <span v-else-if="item.spacesAvailable">
                            {{ item.spacesAvailable }} space<span
                              v-if="item.spacesAvailable !== '1'"
                              >s</span
                            >
                            available</span
                          >
                          <span v-else-if="item.limitedAvailability">
                            {{ item.limitedAvailability }} space<span
                              v-if="item.limitedAvailability !== '1'"
                              >s</span
                            >
                            available</span
                          >
                          <span v-else>
                            {{
                              item.availability || currentPriceYear1?.availability
                            }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </template>
                </template>

                <template v-else-if="showFlexibleAvailabilityYear1">
                  <h4>Dates &amp; Availability for {{ year1Label }}</h4>
                  <div class="flexible-availability">
                    <div
                      v-if="currentPriceYear1?.availability"
                      class="trek-availability"
                      :class="
                        currentPriceYear1.availability
                          .toLowerCase()
                          .replace(/\s+/g, '')
                      "
                    >
                      {{ currentPriceYear1.availability }}
                    </div>
                    <div
                      v-if="currentPriceYear1?.dates"
                      class="flexible-dates"
                      v-html="currentPriceYear1.dates"
                    />
                  </div>
                </template>

                <template v-if="showDatesTableYear2">
                  <h4>Dates &amp; Availability for {{ year2Label }}</h4>
                  <template v-if="shouldShowDatesForSelfGuided">
                    <div
                      class="dates-table"
                      :class="{
                        'self-guided-two-column': shouldShowDatesForSelfGuided,
                      }"
                    >
                      <template
                        v-for="(item, index) in selfGuidedDatesYear2"
                        :key="index"
                      >
                        <div
                          v-if="item.type === 'month-header'"
                          class="month-header"
                        >
                          {{ item.month }}
                        </div>
                        <div
                          v-else-if="item.type === 'date-item'"
                          class="table-row"
                        >
                          <div class="dates">{{ item.dates }}</div>
                          <div
                            class="availability"
                            :class="
                              item?.availability
                                ? item.availability
                                    .toLowerCase()
                                    .replace(/\s+/g, '')
                                : ''
                            "
                          >
                            <span
                              v-if="
                                item.availability === 'Fully Booked' ||
                                item.availability === 'No Availability'
                              "
                            >
                              {{ item.availability }}
                            </span>
                            <span v-else-if="item.spacesAvailable">
                              {{ item.spacesAvailable }} space<span
                                v-if="item.spacesAvailable !== '1'"
                                >s</span
                              >
                              available</span
                            >
                            <span v-else-if="item.limitedAvailability">
                              {{ item.limitedAvailability }} space<span
                                v-if="item.limitedAvailability !== '1'"
                                >s</span
                              >
                              available</span
                            >
                            <span v-else> {{ item.availability }}</span>
                          </div>
                        </div>
                      </template>
                    </div>
                  </template>
                  <template v-else>
                    <div class="dates-table">
                      <div
                        v-for="(item, dateIndex) in currentDatesYear2"
                        :key="item.id ?? dateIndex"
                        class="table-row"
                      >
                        <div class="dates">{{ item.dates }}</div>
                        <div
                          class="availability"
                          :class="
                            item?.availability
                              ? item.availability
                                  .toLowerCase()
                                  .replace(/\s+/g, '')
                              : ''
                          "
                        >
                          <span
                            v-if="
                              item.availability === 'Fully Booked' ||
                              item.availability === 'No Availability'
                            "
                          >
                            {{ item.availability }}
                          </span>
                          <span v-else-if="item.spacesAvailable">
                            {{ item.spacesAvailable }} space<span
                              v-if="item.spacesAvailable !== '1'"
                              >s</span
                            >
                            available</span
                          >
                          <span v-else-if="item.limitedAvailability">
                            {{ item.limitedAvailability }} space<span
                              v-if="item.limitedAvailability !== '1'"
                              >s</span
                            >
                            available</span
                          >
                          <span v-else>
                            {{
                              item.availability || currentPriceYear2?.availability
                            }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </template>
                </template>

                <template v-else-if="showFlexibleAvailabilityYear2">
                  <h4>Dates &amp; Availability for {{ year2Label }}</h4>
                  <div class="flexible-availability">
                    <div
                      v-if="currentPriceYear2?.availability"
                      class="trek-availability"
                      :class="
                        currentPriceYear2.availability
                          .toLowerCase()
                          .replace(/\s+/g, '')
                      "
                    >
                      {{ currentPriceYear2.availability }}
                    </div>
                    <div
                      v-if="currentPriceYear2?.dates"
                      class="flexible-dates"
                      v-html="currentPriceYear2.dates"
                    />
                  </div>
                </template>
                <div
                  v-if="
                    currentIsSelfGuidedTitle && !shouldShowDatesForSelfGuided
                  "
                  class="trek-image"
                >
                  <NuxtPicture
                    v-if="currentTrek?.trek_image"
                    loading="lazy"
                    fit="inside"
                    :src="currentTrek.trek_image.url"
                    :width="700"
                    :height="400"
                    :alt="currentTrek.trek_image.alt"
                    sizes="sm:100vw md:50vw lg:600px"
                  />
                </div>
              </div>
            </div>
          </div>
          <footer class="display-desktop">
            <NuxtLink
              v-if="currentTrekItineraryPath"
              :to="currentTrekItineraryPath"
              class="btn"
              @click="closePackageModal"
            >
              View trek itinerary
            </NuxtLink>
            <NuxtLink
              to="/essential-info/booking-your-trek"
              class="ready-to-book"
              @click="closePackageModal"
            >
              Ready to book?
            </NuxtLink>
            <button
              type="button"
              class="btn enquire-btn"
              @click="openEnquiryModal"
            >
              Enquire now
            </button>
          </footer>
        </div>
      </div>
    </Transition>
    <Transition name="fade" appear>
      <div
        v-if="showPackageModal && !showEnquiryModal"
        v-cloak
        class="backdrop"
        @click="closePackageModal"
      ></div>
    </Transition>
    <Transition name="pop" appear>
      <div
        v-if="showEnquiryModal && currentTrek"
        v-cloak
        class="modal trek-enquiry-modal"
      >
        <button
          class="close-modal"
          aria-label="Close"
          @click="closeEnquiryModal"
        />
        <div class="modal-inner">
          <AppFormTrekEnquiryForm
            :trek-route="enquiryTrekRoute"
            :trek-title="enquiryTrekTitle"
            :trek-price="enquiryPrice"
            :trek-person-day="enquiryPersonDay"
            :trek-dates="enquiryNextDate"
            :places-available="enquirySpaces"
            :availability="enquiryAvailability"
            @close="closeEnquiryModal"
            @success="onEnquirySuccess"
          />
        </div>
      </div>
    </Transition>
    <Transition name="fade" appear>
      <div
        v-if="showEnquiryModal"
        v-cloak
        class="backdrop"
        @click="closeEnquiryModal"
      />
    </Transition>
  </Teleport>
  <p v-if="apiPending" class="trek-packages-status">Loading trek packages…</p>
  <p v-else-if="apiError" class="trek-packages-status trek-packages-status--error">
    Trek packages are temporarily unavailable. Please try again shortly.
  </p>
  <template v-else>
    <h4 v-if="hasMultipleYears">Which year do you want to travel in:</h4>
    <section v-if="hasYear1Data || hasYear2Data" class="year-btns">
      <button
        v-if="hasYear1Data"
        class="btn from-left"
        :class="{ selected: setYear1 }"
        @click.prevent="showYear1"
      >
        {{ year1Label }}
      </button>
      <button
        v-if="hasYear2Data"
        class="btn from-left"
        :class="{ selected: setYear2 }"
        @click.prevent="showYear2"
      >
        {{ year2Label }}
      </button>
    </section>

    <section class="treks-wrapper">
      <section class="treks-nav-wrap" :class="`trek-num-${trekPackagesLength}`">
        <div
          v-for="(trek, index) in trekPackages"
          :key="trek.trek_code || trek.id || index"
          class="trek"
        >
          <button
            class="btn from-left"
            @click.prevent="togglePackageModal(trek, index)"
          >
            <div class="trek-image">
              <NuxtPicture
                v-if="trek?.trek_image"
                loading="lazy"
                fit="inside"
                :src="trek.trek_image.url"
                :width="700"
                :height="400"
                :alt="trek.trek_image.alt"
                sizes="sm:100vw md:50vw lg:600px"
              />
            </div>
            <div class="trek-title">
              <h3>{{ trek?.trek_title }}</h3>
              <div class="trek-price-box"></div>
            </div>
          </button>
        </div>
      </section>
      <!--
        Use v-if (not Transition + v-show): slideup left opacity:0 on the
        absolute price overlays, so year switch looked like missing data.
      -->
      <section
        v-if="hasYear1Data && setYear1"
        class="treks-nav-wrap-prices year1"
        :class="`trek-num-${trekPackagesLength}`"
      >
        <div
          v-for="(trek, index) in priceRowsYear1"
          :key="trek?.trek_code || trek?.id || index"
          class="trek trek-prices"
        >
          <div
            class="trek-availability"
            :class="
              trek?.availability
                ? trek.availability.toLowerCase().replace(/\s+/g, '')
                : ''
            "
          >
            {{ trek?.availability }}
          </div>
          <div v-if="trek" class="trek-price">
            <p
              v-if="isSelfGuidedTrek(trekPackages[index] || trek)"
              class="from-price"
            >
              From
            </p>
            <p class="price">
              &pound;{{ String(trek?.trek_price ?? "").trim() }}
            </p>
            <p class="per">{{ trek?.trek_person_day }}</p>
          </div>
        </div>
      </section>
      <section
        v-if="hasYear2Data && setYear2"
        class="treks-nav-wrap-prices year2"
        :class="`trek-num-${trekPackagesLength}`"
      >
        <div
          v-for="(trek, index) in priceRowsYear2"
          :key="trek?.trek_code || trek?.id || index"
          class="trek trek-prices"
        >
          <div
            class="trek-availability"
            :class="
              trek?.availability
                ? trek.availability.toLowerCase().replace(/\s+/g, '')
                : ''
            "
          >
            {{ trek?.availability }}
          </div>
          <div v-if="trek" class="trek-price">
            <p
              v-if="isSelfGuidedTrek(trekPackages[index] || trek)"
              class="from-price"
            >
              From
            </p>
            <p class="price">
              &pound;{{ String(trek?.trek_price ?? "").trim() }}
            </p>
            <p class="per">{{ trek?.trek_person_day }}</p>
          </div>
        </div>
      </section>

      <!-- SSR-visible departure dates (not modal-only) for both seasons -->
      <section
        v-if="hasYear1Data || hasYear2Data"
        class="trek-departures-seo"
        aria-label="Trek departure dates"
      >
        <div v-if="hasYear1Data" class="trek-departures-seo__year">
          <h3>Departure dates {{ year1Label }}</h3>
          <div
            v-for="(trek, index) in priceRowsYear1"
            :key="`y1-${trek?.trek_code || index}`"
            class="trek-departures-seo__package"
          >
            <h4>
              {{
                trek?.trek_title ||
                trekPackages[index]?.trek_title ||
                trek?.trek_route
              }}
            </h4>
            <ul v-if="toDatesArray(trek?.datesAvailability).length">
              <li
                v-for="(row, dateIndex) in toDatesArray(trek?.datesAvailability)"
                :key="dateIndex"
              >
                <span class="dates">{{ row.dates }}</span>
                <span class="availability">{{
                  row.availability || trek?.availability || ""
                }}</span>
              </li>
            </ul>
            <ul v-else>
              <li>
                <span class="dates"></span>
                <span class="availability">{{
                  trek?.availability || "Dates on request"
                }}</span>
              </li>
            </ul>
          </div>
        </div>
        <div v-if="hasYear2Data" class="trek-departures-seo__year">
          <h3>Departure dates {{ year2Label }}</h3>
          <div
            v-for="(trek, index) in priceRowsYear2"
            :key="`y2-${trek?.trek_code || index}`"
            class="trek-departures-seo__package"
          >
            <h4>
              {{
                trek?.trek_title ||
                trekPackages[index]?.trek_title ||
                trek?.trek_route
              }}
            </h4>
            <ul v-if="toDatesArray(trek?.datesAvailability).length">
              <li
                v-for="(row, dateIndex) in toDatesArray(trek?.datesAvailability)"
                :key="dateIndex"
              >
                <span class="dates">{{ row.dates }}</span>
                <span class="availability">{{
                  row.availability || trek?.availability || ""
                }}</span>
              </li>
            </ul>
            <ul v-else>
              <li>
                <span class="dates"></span>
                <span class="availability">{{
                  trek?.availability || "Dates on request"
                }}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </section>
  </template>
</template>
